import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChartBox, StatusCards } from "@unified-checkout/design-system";
import { SkeletonLoader } from "@unified-checkout/design-system";
import type { IHomeApisParams } from "@app/dashboard/types/innerLayout/home";
import type { IStatusCard } from "libs/design-system/src/types/charts/chart/status_cards";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { ComponentError } from "@app/dashboard/components/shared/ComponentError";
import { getPerformanceHighlights } from "@app/dashboard/services/inner_layout/home/charts";
import { useFiltersStore } from "@app/dashboard/store/home/filters";
import { useLangStore } from "@app/dashboard/store/lang";

const PerformanceHighlights = ({ apiParams }: { apiParams: IHomeApisParams }) => {

  const queryClient = useQueryClient();

  const { t } = useTranslation();
  const { filters } = useFiltersStore();
  const { lang } = useLangStore();

  const {
    isLoading,
    isError,
    data: response,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_PERFORMANCE_HIGHLIGHTS, filters],
    queryFn: () => getPerformanceHighlights(apiParams),
    enabled: Object.values(filters).every(value => value !== undefined)
  });

  const chartData = response?.data;

  const list: IStatusCard[] = chartData?.data?.map((item: any) => ({
    text: item[`title_${lang}`],
    status: item.status,
  }));

  useEffect(() => {
    return () => queryClient.removeQueries({ queryKey: [QUERY_KEYS.GET_PERFORMANCE_HIGHLIGHTS] });
  }, []);

  return (

    (
      isError ?
        <ChartBox>
          <ComponentError text={t('innerLayout.errors.unableToRetrieveData')} />
        </ChartBox>
        :
        isLoading ?
          <SkeletonLoader noOfRows={8} />
          :
          (
            response ?
              <ChartBox
                title={chartData[`${lang}_title`]}
                info={chartData[`info_${lang}`]}
                addOverlayOnOverflow
              >
                <StatusCards
                  list={list}
                  emptyStateText={t('innerLayout.homePage.charts.emptyStates.noPerformanceAlerts')}
                />
              </ChartBox>
              :
              <SkeletonLoader noOfRows={8} />
          )
    )
  )
}

export default PerformanceHighlights;